import React, { useEffect, useState } from 'react';
import { Check, X, ArrowRight, BookOpen, PenTool, FileText, CheckSquare, Sparkles, ChevronDown, ShieldCheck, Clock } from 'lucide-react';
const KintsugiDivider = ({
  className = ''
}) => <div className={`kintsugi-divider ${className}`} aria-hidden="true" data-id="element-0" />;
const GoldHighlight = ({
  children
}: {
  children: React.ReactNode;
}) => <span className="relative inline-block" data-id="element-1">
    <span className="relative z-10" data-id="element-2">{children}</span>
    <span className="absolute bottom-1 left-0 w-full h-2 bg-gold/30 -z-10 transform -rotate-1" data-id="element-3"></span>
  </span>;
const CTAButton = ({
  href = '#precio',
  children,
  className = '',
  onClick
}: {
  href?: string;
  onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  children: React.ReactNode;
  className?: string;
}) => <a href={href} onClick={e => {
  if (onClick) {
    onClick(e);
    return;
  }
  if (href.startsWith('#')) {
    e.preventDefault();
    const target = document.getElementById(href.slice(1));
    if (target) {
      target.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  }
}} className={`inline-flex items-center justify-center px-8 py-4 bg-gold hover:bg-gold-light text-navy font-serif font-semibold text-lg tracking-wide transition-all duration-300 shadow-[0_4px_20px_rgba(201,169,97,0.3)] hover:shadow-[0_6px_25px_rgba(201,169,97,0.5)] hover:-translate-y-1 ${className}`} data-id="element-4">
    {children}
    <ArrowRight className="ml-2 w-5 h-5" data-id="element-5" />
  </a>;
export function App() {
  return <div className="min-h-screen font-sans selection:bg-gold/30 selection:text-navy" data-id="element-6">
      {/* HERO SECTION */}
      <section className="relative pt-20 pb-24 md:pt-32 md:pb-32 overflow-hidden" data-id="element-7">
        <div className="max-w-7xl mx-auto px-6 lg:px-8" data-id="element-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-8 items-center" data-id="element-9">
            <div className="max-w-2xl" data-id="element-10">
              <div className="inline-flex items-center space-x-2 mb-6" data-id="element-11">
                <span className="h-px w-8 bg-gold" data-id="element-12"></span>
                <span className="text-gold-dark font-semibold tracking-widest uppercase text-sm" data-id="element-13">
                  Libro Digital + Sistema
                </span>
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold text-navy leading-tight mb-6" data-id="element-14">
                EL GUION ROTO: <br data-id="element-15" />
                <span className="text-3xl md:text-4xl lg:text-5xl font-normal italic text-navy-light" data-id="element-16">
                  Los 3 Bloqueos de la Niñez que Frenan tu Prosperidad
                </span>
              </h1>
              <p className="text-lg md:text-xl text-navy/80 mb-10 leading-relaxed" data-id="element-17">
                Identifica el{' '}
                <GoldHighlight data-id="element-18">"Contrato de Escasez"</GoldHighlight> que
                firmaste en tu infancia y elimina el techo invisible que detiene
                tu crecimiento financiero hoy mismo.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center" data-id="element-19">
                <CTAButton data-id="element-20">¡QUIERO RE-ESCRIBIR MI GUION AHORA!</CTAButton>
                <div className="flex items-center text-sm text-navy/60 mt-2 sm:mt-0" data-id="element-21">
                  <ShieldCheck className="w-4 h-4 mr-1 text-gold" data-id="element-22" />
                  Pago seguro. Acceso instantáneo.
                </div>
              </div>
            </div>
            <div className="relative lg:ml-auto" data-id="element-23">
              <div className="absolute inset-0 bg-gold/10 blur-3xl rounded-full transform translate-x-10 translate-y-10" data-id="element-24"></div>
              <img src="https://cdn.magicpatterns.com/uploads/7o7EacRDKbHFfokkzBuJrf/promt_Iagen_Guion_Roto.png" alt="El Guion Roto en un iPad sobre una mesa de mármol" className="relative z-10 w-full max-w-md mx-auto lg:max-w-full rounded-sm shadow-2xl" data-id="element-25" />
            </div>
          </div>
        </div>
      </section>

      {/* HOOK & PROBLEM SECTION */}
      <section className="bg-navy text-cream py-24 relative" data-id="element-26">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-gold to-transparent opacity-50" data-id="element-27"></div>
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center" data-id="element-28">
          <h2 className="text-3xl md:text-4xl font-serif font-medium mb-12 leading-relaxed" data-id="element-29">
            ¿Alguna vez has sentido que, sin importar cuánto trabajes o qué tan
            buena seas en lo que haces,{' '}
            <span className="text-gold italic" data-id="element-30">
              siempre llegas a un límite que no puedes superar?
            </span>
          </h2>
          <p className="text-lg text-cream/80 mb-16 leading-relaxed max-w-3xl mx-auto" data-id="element-31">
            Como si existiera un termostato invisible que, cada vez que empiezas
            a progresar, activa una crisis, un gasto inesperado o un episodio de
            dudas que te devuelve exactamente al mismo punto de estancamiento.
          </p>
        </div>

        <div className="max-w-5xl mx-auto px-6 lg:px-8" data-id="element-32">
          <div className="bg-cream text-navy p-8 md:p-12 relative" data-id="element-33">
            <div className="absolute top-0 left-0 w-2 h-full bg-gold" data-id="element-34"></div>
            <h3 className="text-2xl md:text-3xl font-serif font-bold mb-6" data-id="element-35">
              El problema no es tu falta de talento...
            </h3>
            <p className="text-lg mb-6 leading-relaxed" data-id="element-36">
              Ni el mercado, ni la economía de tu país. El problema es que estás
              intentando construir un edificio de éxito sobre un guion de
              supervivencia que se escribió en tu mente cuando tenías 7 años.
            </p>
            <p className="text-lg leading-relaxed text-navy/80" data-id="element-37">
              Si creciste viendo a tus padres pelear por dinero, si escuchaste
              que "los ricos no entran al cielo", o si aprendiste que para ser
              amada debías ser invisible y no causar problemas, tu sistema
              nervioso hoy interpreta la abundancia como un peligro.{' '}
              <strong className="font-semibold text-navy" data-id="element-38">
                Para tu cerebro, prosperar significa traicionar a tu familia o
                ponerte en riesgo. Por eso te saboteas.
              </strong>
            </p>
          </div>
        </div>
      </section>

      {/* WHY IT DIDN'T WORK */}
      <section className="py-24 bg-cream" data-id="element-39">
        <div className="max-w-7xl mx-auto px-6 lg:px-8" data-id="element-40">
          <div className="text-center mb-16" data-id="element-41">
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-navy mb-4" data-id="element-42">
              Por qué lo que has intentado no ha funcionado
            </h2>
            <KintsugiDivider className="w-24 mx-auto" data-id="element-43" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-44">
            {[{
            title: 'Las afirmaciones positivas',
            desc: 'No funcionan porque intentan convencer a tu mente lógica, pero el bloqueo está grabado en tu sistema nervioso.'
          }, {
            title: 'Los cursos de marketing',
            desc: 'Fallan porque, aunque sepas qué hacer, tu "guion de invisibilidad" te impide ejecutar las acciones que te harían destacar.'
          }, {
            title: 'Ahorrar con esfuerzo',
            desc: 'No te da paz porque tu guion de carencia te hace sentir que, en cualquier momento, "algo malo va a pasar".'
          }].map((item, idx) => <div key={idx} className="bg-white p-8 border border-gold/20 shadow-sm relative group hover:shadow-md transition-shadow" data-id="element-45">
                <div className="absolute top-0 left-0 w-full h-1 bg-gold transform scale-x-0 group-hover:scale-x-100 transition-transform origin-left" data-id="element-46"></div>
                <X className="w-8 h-8 text-gold mb-6" data-id="element-47" />
                <h3 className="text-xl font-serif font-bold text-navy mb-4" data-id="element-48">
                  {item.title}
                </h3>
                <p className="text-navy/70 leading-relaxed" data-id="element-49">{item.desc}</p>
              </div>)}
          </div>
        </div>
      </section>

      {/* UNIQUE MECHANISM */}
      <section className="py-24 bg-navy text-cream relative overflow-hidden" data-id="element-50">
        <div className="absolute -right-64 -top-64 w-128 h-128 bg-gold/5 rounded-full blur-3xl" data-id="element-51"></div>
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center relative z-10" data-id="element-52">
          <Sparkles className="w-12 h-12 text-gold mx-auto mb-8" data-id="element-53" />
          <h2 className="text-3xl md:text-4xl font-serif font-bold mb-8" data-id="element-54">
            La clave es el <span className="text-gold" data-id="element-55">Efecto Impronta</span>
          </h2>
          <p className="text-xl leading-relaxed mb-8 text-cream/90" data-id="element-56">
            A través de la{' '}
            <strong className="text-gold font-normal italic" data-id="element-57">
              Neuro-Escritura
            </strong>{' '}
            y la{' '}
            <strong className="text-gold font-normal italic" data-id="element-58">
              Reparentalización
            </strong>
            , vamos a localizar los 3 Guiones maestros (Carencia, Invisibilidad
            y Esfuerzo Sacrificado) para desconectar la carga emocional del
            pasado.
          </p>
          <p className="text-lg text-cream/70" data-id="element-59">
            No vamos a "pensar positivo", vamos a re-escribir el contrato
            subconsciente que rige tus decisiones actuales.
          </p>
        </div>
      </section>

      {/* WHAT YOU GET */}
      <section className="py-24 bg-cream" data-id="element-60">
        <div className="max-w-7xl mx-auto px-6 lg:px-8" data-id="element-61">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-62">
            <div data-id="element-63">
              <h2 className="text-3xl md:text-4xl font-serif font-bold text-navy mb-6" data-id="element-64">
                Lo que obtienes hoy
              </h2>
              <p className="text-lg text-navy/70 mb-10" data-id="element-65">
                El sistema completo para identificar y romper tus bloqueos
                heredados.
              </p>

              <div className="space-y-8" data-id="element-66">
                {[{
                icon: BookOpen,
                title: 'Guía Digital El Guion Roto',
                desc: 'El manual completo para identificar y romper tus bloqueos heredados.'
              }, {
                icon: PenTool,
                title: 'Protocolo de Re-Escritura',
                desc: 'El método paso a paso para crear tu nueva identidad financiera.'
              }, {
                icon: FileText,
                title: 'Hoja de Trabajo "Tu Nuevo Contrato"',
                desc: 'El documento de liberación emocional para soltar lealtades invisibles.'
              }, {
                icon: CheckSquare,
                title: 'Checklist de Conciencia Diaria',
                desc: 'Para detectar y detener el autosabotaje antes de que vacíe tu cuenta.'
              }, {
                icon: Sparkles,
                title: 'Inventario de Facilidad',
                desc: 'La herramienta para encontrar tu zona de genio y dejar de sufrir por dinero.'
              }].map((item, idx) => <div key={idx} className="flex items-start" data-id="element-67">
                    <div className="flex-shrink-0 mt-1" data-id="element-68">
                      <div className="w-10 h-10 rounded-full bg-gold/20 flex items-center justify-center" data-id="element-69">
                        <item.icon className="w-5 h-5 text-gold-dark" data-id="element-70" />
                      </div>
                    </div>
                    <div className="ml-4" data-id="element-71">
                      <h4 className="text-xl font-serif font-bold text-navy" data-id="element-72">
                        {item.title}
                      </h4>
                      <p className="mt-1 text-navy/70" data-id="element-73">{item.desc}</p>
                    </div>
                  </div>)}
              </div>
            </div>

            <div className="relative" data-id="element-74">
              <div className="aspect-[4/5] bg-navy rounded-sm p-8 flex flex-col justify-center items-center text-center relative overflow-hidden shadow-2xl" data-id="element-75">
                <div className="absolute inset-0 opacity-20" style={{
                backgroundImage: 'radial-gradient(#C9A961 1px, transparent 1px)',
                backgroundSize: '20px 20px'
              }} data-id="element-76"></div>
                <h3 className="text-gold font-serif text-2xl mb-4 relative z-10" data-id="element-77">
                  Valor Total
                </h3>
                <div className="text-cream text-6xl font-serif font-bold mb-8 relative z-10" data-id="element-78">
                  $27 <span className="text-2xl" data-id="element-79">USD</span>
                </div>
                <CTAButton href="#offer" className="w-full relative z-10" data-id="element-80">
                  SÍ, LO QUIERO AHORA
                </CTAButton>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHO THIS IS FOR / NOT FOR */}
      <section className="py-24 bg-white border-y border-gold/20" data-id="element-81">
        <div className="max-w-7xl mx-auto px-6 lg:px-8" data-id="element-82">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 lg:gap-24" data-id="element-83">
            {/* FOR */}
            <div data-id="element-84">
              <h2 className="text-2xl md:text-3xl font-serif font-bold text-navy mb-8 flex items-center" data-id="element-85">
                <span className="w-8 h-px bg-gold mr-4" data-id="element-86"></span>
                Esto es para ti si...
              </h2>
              <ul className="space-y-6" data-id="element-87">
                {['Mujeres emprendedoras que se sienten estancadas en un nivel de ingresos desde hace meses o años.', 'Profesionales que sienten culpa o miedo al cobrar lo que realmente vale su trabajo.', 'Mujeres que sienten que "el dinero se les escapa de las manos" no importa cuánto ganen.', 'Cualquier persona que detecte que está repitiendo los mismos patrones financieros de sus padres.'].map((text, idx) => <li key={idx} className="flex items-start" data-id="element-88">
                    <Check className="w-6 h-6 text-gold flex-shrink-0 mr-3 mt-0.5" data-id="element-89" />
                    <span className="text-navy/80 leading-relaxed" data-id="element-90">{text}</span>
                  </li>)}
              </ul>
            </div>

            {/* NOT FOR */}
            <div data-id="element-91">
              <h2 className="text-2xl md:text-3xl font-serif font-bold text-navy/50 mb-8 flex items-center" data-id="element-92">
                <span className="w-8 h-px bg-navy/20 mr-4" data-id="element-93"></span>
                Esto NO es para ti si...
              </h2>
              <ul className="space-y-6" data-id="element-94">
                {['Personas que buscan una fórmula mágica para hacerse millonarias sin trabajar en su mentalidad.', 'Quienes no están dispuestos a mirar honestamente su historia familiar.', 'Personas que prefieren seguir culpando a la suerte o al gobierno de su situación.'].map((text, idx) => <li key={idx} className="flex items-start opacity-70" data-id="element-95">
                    <X className="w-6 h-6 text-navy/40 flex-shrink-0 mr-3 mt-0.5" data-id="element-96" />
                    <span className="text-navy/60 leading-relaxed" data-id="element-97">{text}</span>
                  </li>)}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-24 bg-cream" data-id="element-98">
        <div className="max-w-7xl mx-auto px-6 lg:px-8" data-id="element-99">
          <div className="text-center mb-16" data-id="element-100">
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-navy mb-4" data-id="element-101">
              Cómo funciona el proceso
            </h2>
            <KintsugiDivider className="w-24 mx-auto" data-id="element-102" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative" data-id="element-103">
            <div className="hidden md:block absolute top-1/2 left-0 w-full h-px bg-gold/30 -translate-y-1/2 z-0" data-id="element-104"></div>

            {[{
            step: '01',
            title: 'Identificas',
            desc: 'Usas el Libro para localizar cuál de los 3 guiones está operando en tu vida hoy.'
          }, {
            step: '02',
            title: 'Interrumpes',
            desc: 'Aplicas la técnica de Neuro-Escritura para cortar el cable emocional del trauma infantil.'
          }, {
            step: '03',
            title: 'Re-escribes',
            desc: 'Instalas una nueva verdad de abundancia que tu sistema nervioso acepte como segura.'
          }].map((item, idx) => <div key={idx} className="relative z-10 bg-cream border border-gold/20 p-8 text-center shadow-sm" data-id="element-105">
                <div className="w-16 h-16 mx-auto bg-navy text-gold font-serif text-2xl flex items-center justify-center rounded-full mb-6 border-4 border-cream shadow-md" data-id="element-106">
                  {item.step}
                </div>
                <h3 className="text-xl font-serif font-bold text-navy mb-4" data-id="element-107">
                  {item.title}
                </h3>
                <p className="text-navy/70 leading-relaxed" data-id="element-108">{item.desc}</p>
              </div>)}
          </div>
        </div>
      </section>

      {/* PROOF & FAQ */}
      <section className="py-24 bg-navy text-cream" data-id="element-109">
        <div className="max-w-4xl mx-auto px-6 lg:px-8" data-id="element-110">
          {/* PROOF */}
          <div className="mb-24 text-center" data-id="element-111">
            <h2 className="text-2xl md:text-3xl font-serif font-bold mb-6 text-gold" data-id="element-112">
              El cambio en los primeros 7 días
            </h2>
            <p className="text-lg leading-relaxed text-cream/90 italic" data-id="element-113">
              "Al liberar la carga emocional de la 'lealtad invisible', dejarás
              de ver el éxito como una amenaza. Muchas usuarias reportan que, al
              quitarse el peso de la culpa materna o el miedo a ser vistas,
              aparecen oportunidades y clientes que antes parecían
              'bloqueados'."
            </p>
          </div>

          <KintsugiDivider className="mb-24 opacity-30" data-id="element-114" />

          {/* FAQ */}
          <div data-id="element-115">
            <h2 className="text-3xl font-serif font-bold mb-12 text-center" data-id="element-116">
              Preguntas Frecuentes
            </h2>
            <div className="space-y-8" data-id="element-117">
              {[{
              q: '¿Esto es terapia?',
              a: 'No. Es una herramienta de desarrollo personal y reprogramación mental enfocada a resultados prácticos en tu vida y economía.'
            }, {
              q: '¿Necesito mucho tiempo?',
              a: 'No. Está diseñado para mujeres ocupadas. Puedes leerlo en una tarde y aplicar los ejercicios en 15 minutos al día.'
            }, {
              q: '¿Me servirá si no recuerdo bien mi infancia?',
              a: 'Sí, porque el libro te enseña a identificar el guion por tus resultados PRESENTES.'
            }].map((faq, idx) => <div key={idx} className="border-b border-gold/20 pb-6" data-id="element-118">
                  <h4 className="text-xl font-serif font-bold text-gold mb-3 flex items-center" data-id="element-119">
                    <ChevronDown className="w-5 h-5 mr-2" data-id="element-120" />
                    {faq.q}
                  </h4>
                  <p className="text-cream/80 pl-7 leading-relaxed" data-id="element-121">{faq.a}</p>
                </div>)}
            </div>
          </div>
        </div>
      </section>

      {/* OFFER & CTA */}
      <section id="offer" className="py-32 bg-cream relative overflow-hidden" data-id="element-122">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-3xl h-full bg-gold/5 blur-3xl rounded-full" data-id="element-123"></div>
        <div className="max-w-3xl mx-auto px-6 lg:px-8 text-center relative z-10" data-id="element-124">
          <h2 className="text-4xl md:text-5xl font-serif font-bold text-navy mb-8" data-id="element-125">
            Es momento de dejar de pelear contra un muro invisible.
          </h2>
          <p className="text-xl text-navy/80 mb-12" data-id="element-126">
            Recupera el control de tu destino financiero hoy mismo.
          </p>

          <div id="precio" className="bg-white p-8 md:p-12 border border-gold/30 shadow-xl mb-10 scroll-mt-24" data-id="element-127">
            <h3 className="text-2xl font-serif text-navy mb-2" data-id="element-128">
              Sistema Completo "El Guion Roto"
            </h3>
            <div className="inline-flex items-center gap-2 bg-navy text-gold text-xs font-semibold tracking-widest uppercase px-3 py-1 mb-4" data-id="element-129">
              <span className="w-1.5 h-1.5 bg-gold rounded-full" data-id="element-130"></span>
              Oferta por tiempo limitado
            </div>
            <div className="flex justify-center items-baseline gap-3 mb-2" data-id="element-131">
              <span className="text-2xl font-serif text-navy/40 line-through decoration-gold/70" data-id="element-132">
                $27
              </span>
              <span className="text-6xl font-serif font-bold text-navy" data-id="element-133">
                $6.39
              </span>
              <span className="text-xl text-navy/60" data-id="element-134">USD</span>
            </div>
            <p className="text-sm text-gold-dark font-semibold tracking-wide mb-8" data-id="element-135">
              Ahorras más del 75% hoy
            </p>
            <CTAButton href="https://protocoloshift.com/cart/55714736373833:1" className="w-full md:w-auto text-xl py-5 px-10" data-id="element-136">
              ¡QUIERO RE-ESCRIBIR MI GUION AHORA!
            </CTAButton>
            <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-4 text-sm text-navy/60" data-id="element-137">
              <span className="flex items-center" data-id="element-138">
                <ShieldCheck className="w-4 h-4 mr-1 text-gold" data-id="element-139" /> Pago 100%
                Seguro
              </span>
              <span className="hidden sm:inline" data-id="element-140">•</span>
              <span className="flex items-center" data-id="element-141">
                <Clock className="w-4 h-4 mr-1 text-gold" data-id="element-142" /> Acceso Inmediato
                PDF
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <SiteFooter data-id="element-143" />
    </div>;
}
type LegalDoc = 'privacy' | 'terms' | null;
const LEGAL_CONTENT: Record<Exclude<LegalDoc, null>, {
  title: string;
  sections: {
    heading: string;
    body: string;
  }[];
}> = {
  privacy: {
    title: 'Política de Privacidad',
    sections: [{
      heading: '1. Información que recopilamos',
      body: 'En El Guion Roto recopilamos únicamente la información necesaria para procesar tu compra y entregarte el producto digital: nombre, correo electrónico y datos de pago procesados a través de pasarelas seguras de terceros. No almacenamos información de tu tarjeta en nuestros servidores.'
    }, {
      heading: '2. Uso de la información',
      body: 'Usamos tus datos exclusivamente para entregarte el producto adquirido, enviarte recursos complementarios relacionados con tu compra y notificarte sobre actualizaciones del material. No vendemos, alquilamos ni compartimos tu información personal con terceros con fines comerciales.'
    }, {
      heading: '3. Cookies y datos de navegación',
      body: 'Utilizamos cookies y tecnologías similares para mejorar tu experiencia de navegación y medir el rendimiento de la página. Puedes desactivar las cookies desde la configuración de tu navegador en cualquier momento.'
    }, {
      heading: '4. Tus derechos',
      body: 'Tienes derecho a acceder, rectificar, eliminar y portar tus datos personales. También puedes oponerte al tratamiento de tus datos o solicitar su limitación enviando tu petición desde el mismo correo con el que realizaste la compra.'
    }, {
      heading: '5. Seguridad',
      body: 'Implementamos medidas técnicas y organizativas razonables para proteger tu información personal contra accesos no autorizados, pérdida o alteración. Sin embargo, ningún sistema en internet es 100% seguro.'
    }, {
      heading: '6. Cambios a esta política',
      body: 'Podemos actualizar esta política periódicamente. La fecha de la última modificación aparecerá siempre al pie de este documento. El uso continuado del sitio después de cualquier cambio constituye tu aceptación de los términos actualizados.'
    }]
  },
  terms: {
    title: 'Términos y Condiciones',
    sections: [{
      heading: '1. Sobre el producto',
      body: 'El Guion Roto es un producto digital de desarrollo personal en formato PDF. No constituye terapia psicológica, asesoría financiera profesional ni promesa de resultados económicos específicos. Es una herramienta de reflexión y crecimiento personal.'
    }, {
      heading: '2. Licencia de uso',
      body: 'Al adquirir el producto recibes una licencia personal, intransferible y no exclusiva para su uso individual. Está prohibida la reventa, redistribución, copia o compartición total o parcial del material con terceros sin autorización expresa por escrito.'
    }, {
      heading: '3. Política de reembolso',
      body: 'Debido a la naturaleza digital del producto y al acceso inmediato a su contenido, todas las ventas son finales. No se realizan reembolsos una vez completada la transacción y entregado el material.'
    }, {
      heading: '4. Acceso al contenido',
      body: 'Recibirás el acceso al producto en formato PDF de manera inmediata tras completar tu compra, al correo electrónico proporcionado durante el proceso de pago. Es tu responsabilidad verificar que el correo sea correcto y revisar tu carpeta de spam.'
    }, {
      heading: '5. Limitación de responsabilidad',
      body: 'El contenido se ofrece "tal cual" con fines informativos y de desarrollo personal. Los resultados varían según el compromiso y aplicación de cada persona. No nos hacemos responsables de decisiones financieras, emocionales o personales tomadas en base al material.'
    }, {
      heading: '6. Propiedad intelectual',
      body: 'Todos los textos, ejercicios, marcos conceptuales y diseños incluidos en El Guion Roto son propiedad intelectual del autor y están protegidos por las leyes de derechos de autor aplicables.'
    }, {
      heading: '7. Jurisdicción',
      body: 'Cualquier disputa relacionada con el producto o estos términos se regirá por las leyes aplicables al país de residencia del titular del producto, sin perjuicio de los derechos del consumidor que correspondan según la legislación local.'
    }]
  }
};
function SiteFooter() {
  const [openDoc, setOpenDoc] = useState<LegalDoc>(null);
  return <>
      <footer className="bg-navy-dark text-cream/60 py-10" data-id="element-144">
        <div className="max-w-7xl mx-auto px-6" data-id="element-145">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6" data-id="element-146">
            <p className="text-sm text-center md:text-left" data-id="element-147">
              © {new Date().getFullYear()} El Guion Roto. Todos los derechos
              reservados.
            </p>
            <nav className="flex items-center justify-center gap-6 text-sm" data-id="element-148">
              <button type="button" onClick={() => setOpenDoc('privacy')} className="hover:text-gold transition-colors underline-offset-4 hover:underline" data-id="element-149">
                Política de Privacidad
              </button>
              <span className="text-cream/20" aria-hidden="true" data-id="element-150">
                |
              </span>
              <button type="button" onClick={() => setOpenDoc('terms')} className="hover:text-gold transition-colors underline-offset-4 hover:underline" data-id="element-151">
                Términos y Condiciones
              </button>
            </nav>
          </div>
        </div>
      </footer>

      <LegalModal doc={openDoc} onClose={() => setOpenDoc(null)} data-id="element-152" />
    </>;
}
function LegalModal({
  doc,
  onClose
}: {
  doc: LegalDoc;
  onClose: () => void;
}) {
  useEffect(() => {
    if (!doc) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleKey);
    };
  }, [doc, onClose]);
  if (!doc) return null;
  const content = LEGAL_CONTENT[doc];
  return <div role="dialog" aria-modal="true" aria-labelledby="legal-modal-title" className="fixed inset-0 z-50 flex items-center justify-center p-4 md:p-8" data-id="element-153">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-navy-dark/70 backdrop-blur-sm animate-fade-in" onClick={onClose} aria-hidden="true" data-id="element-154" />

      {/* Modal */}
      <div className="relative bg-cream max-w-3xl w-full max-h-[85vh] shadow-2xl border border-gold/30 flex flex-col animate-modal-in" data-id="element-155">
        {/* Header */}
        <div className="flex items-start justify-between p-6 md:p-8 border-b border-gold/20" data-id="element-156">
          <div data-id="element-157">
            <div className="inline-flex items-center space-x-2 mb-2" data-id="element-158">
              <span className="h-px w-6 bg-gold" data-id="element-159"></span>
              <span className="text-gold-dark font-semibold tracking-widest uppercase text-xs" data-id="element-160">
                Documento Legal
              </span>
            </div>
            <h3 id="legal-modal-title" className="text-2xl md:text-3xl font-serif font-bold text-navy" data-id="element-161">
              {content.title}
            </h3>
          </div>
          <button type="button" onClick={onClose} aria-label="Cerrar" className="flex-shrink-0 ml-4 w-10 h-10 flex items-center justify-center text-navy/60 hover:text-navy hover:bg-gold/10 transition-colors rounded-full" data-id="element-162">
            <X className="w-5 h-5" data-id="element-163" />
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto px-6 md:px-8 py-8 space-y-6" data-id="element-164">
          {content.sections.map((section, idx) => <div key={idx} data-id="element-165">
              <h4 className="text-lg font-serif font-bold text-navy mb-2" data-id="element-166">
                {section.heading}
              </h4>
              <p className="text-navy/75 leading-relaxed" data-id="element-167">{section.body}</p>
            </div>)}
          <div className="pt-4 border-t border-gold/20" data-id="element-168">
            <p className="text-xs text-navy/50 italic" data-id="element-169">
              Última actualización:{' '}
              {new Date().toLocaleDateString('es-ES', {
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}
            </p>
          </div>
        </div>
      </div>
    </div>;
}